import { FadeInCommand, TimelineSlot } from '..';
import { Log } from '../../AuxillaryService/Log';
import { getZoneById } from '../../Display';
import * as Platform from '../../Platform';
import { ReachAPI } from '../../ReachApi';
import { getAcquireUrlHeaders } from '../../ReachApiV2/endpoints';
import { Signage } from '../../Signage';
import { asyncTimeout } from '../../Utility/AsyncTimeout';
import '../../Utility/extendBuiltIns/RegExp.escape';
import { regExpEscape } from '../../Utility/RegExpEscape';
import { WidgetType } from '../types';
import { WidgetWithScheduler } from '../WidgetWithScheduler';
import {
    IFrameWidgetConfiguration,
    IFrameWidgetConfigurationDetails,
    LoginLogicData,
    LoginVerification,
    SequenceConfig,
    SequenceStepTypes,
    StepClick,
    StepExecuteJs,
    StepExecuteOnElement,
    StepSetValue,
    StepWait,
    StepWaitForElements,
    ZodIFrameWidgetConfigurationDetails,
} from './types';
import { VirtualIframe } from './VirtualIframe';
import { WindowedIframe } from './WindowedIframe';
import './iframe.scss';
import runFromZipLoadingHtml from './runFromZipLoadingHtml.html';
import zipSiteCachingFailedHtml from './zipSiteCachingFailedHtml.html';
import { cacheRunFromZipSite, isSiteZipInCache, updateCachedRunFromZipSite } from './cache';
import { isNullish } from '../../Utility/Nullish';
import { extractRunFromZipConfig } from './extractRunFromZipConfig';

const ZIP_FILE_UPDATE_INTERVAL = 28_800_000; //8 hours in ms

export class IframeWidget extends WidgetWithScheduler {
    readonly type: WidgetType.IFRAME = WidgetType.IFRAME;
    declare readonly config: IFrameWidgetConfigurationDetails;

    /**
     * Widget Width
     */
    #zoneWidth: number;

    /**
     * Widget Height
     */
    #zoneHeight: number;

    /**
     * is true if the widget has a refresh interval
     */
    #shouldBeRefreshing = false;

    /**
     * contains the data obtained for the login logic
     */
    logicData?: LoginLogicData;

    /**
     * contains the data obtained for the login sequence
     */
    #sequenceConfig?: SequenceConfig;

    /**
     * Undefined until widget is initialized.
     */
    #virtualIframe?: VirtualIframe | WindowedIframe;

    /**
     * Has the login script started.
     */
    loginScriptStarted = false;

    /**
     * Holds the password query string.
     */
    #passwordQuery?: string;

    /**
     * Have the postLoginActions been ran.
     */
    #postLoginActionsRan = false;

    /**
     * When defined, stores the HTML source to use for the IFrame in lieu of a src href. This is
     * used by the IFrame Run From Zip feature.
     */
    #srcDocContent: Document | null = null;

    /**
     * Date/time at which to refresh/reload the iframe. `null` means never.
     */
    #nextRefreshAt: number | null = null;

    /**
     * Date/time at which to check if there is an updated version of the zip file on the server
     * (for IFrame Run From Zip).
     */
    #nextZipFileUpdateAt: number | null = null;

    constructor(widgetConfiguration: IFrameWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);

        this.schedulerRunInterval = 60; //enable timer

        const zone = getZoneById(zoneId);
        this.#zoneWidth = zone.width;
        this.#zoneHeight = zone.height;
    }

    /**
     * Verifies configuration details using ZOD.
     */
    protected validateConfigurationDetails(
        maybeIFrameWidgetConfigurationDetails: IFrameWidgetConfigurationDetails
    ): IFrameWidgetConfigurationDetails {
        return ZodIFrameWidgetConfigurationDetails.parse(maybeIFrameWidgetConfigurationDetails);
    }

    /**
     * Setup iFrame first time.
     */
    async initialize(): Promise<void> {
        await super.initialize();

        if (this.config.refresh_interval > 0) {
            this.#nextRefreshAt = Date.now() + this.config.refresh_interval * 60_000;
            this.#shouldBeRefreshing = true;
        }

        if (this.config.run_from_zip || this.config.url.startsWith('https://sites.reach.local')) {
            Log.Debug(
                `IFrame widget '${this.name}' (widget id=${this.id}) with URL '${this.config.url}' is configured for Run From Zip`,
                null,
                __fileline
            );

            this.#nextZipFileUpdateAt = Date.now() + ZIP_FILE_UPDATE_INTERVAL;

            this.setSrcDocContent(runFromZipLoadingHtml);

            void this.setupRunFromZip();
        }

        this.handleFrameErrors();

        this.#virtualIframe = IframeWidget.createInstance(this);
    }

    private setSrcDocContent(htmlContent: Document | string): void {
        if (typeof htmlContent === 'string') {
            this.#srcDocContent = new DOMParser().parseFromString(htmlContent, 'text/html');
        } else {
            this.#srcDocContent = htmlContent;
        }
    }

    private async setupRunFromZip(): Promise<void> {
        const { encodedOriginUrl, decodedOriginUrl, indexFile } = extractRunFromZipConfig(
            this.config
        );

        if (await isSiteZipInCache(decodedOriginUrl)) {
            Log.Debug(
                `Run From Zip site '${decodedOriginUrl}' is already in cache.`,
                null,
                __fileline
            );

            await this.loadCachedZipSiteIntoIframe(encodedOriginUrl, indexFile);
        } else {
            try {
                await cacheRunFromZipSite(decodedOriginUrl);
            } catch (_) {
                this.setSrcDocContent(zipSiteCachingFailedHtml);
            }

            await this.loadCachedZipSiteIntoIframe(encodedOriginUrl, indexFile);
        }

        void this.setIframe();
    }

    private async updateRunFromZipSource(forceUpdate = false): Promise<void> {
        const { encodedOriginUrl, decodedOriginUrl, indexFile } = extractRunFromZipConfig(
            this.config
        );

        if (await isSiteZipInCache(decodedOriginUrl)) {
            if (await updateCachedRunFromZipSite(decodedOriginUrl, forceUpdate)) {
                Log.Debug(
                    `Run From Zip site '${decodedOriginUrl}' was updated; loading new site into IFrame '${this.name}' (widget id=${this.id})`,
                    null,
                    __fileline
                );

                await this.loadCachedZipSiteIntoIframe(encodedOriginUrl, indexFile);

                void this.setIframe();
            }
        } else {
            Log.Warning(
                `Run From Zip site '${decodedOriginUrl}' is not in cache; will not attempt an update.`,
                null,
                __fileline
            );
        }
    }

    private async loadCachedZipSiteIntoIframe(
        encodedOriginUrl: string,
        indexFile: string
    ): Promise<void> {
        const iframeSiteBase = `https://sites.reach.local/${encodedOriginUrl}/`;
        const cache = await caches.open('sites');
        const indexFilePath = new URL(indexFile, iframeSiteBase).toString();
        const indexFileContents = await cache.match(new Request(indexFilePath));

        if (undefined === indexFileContents) {
            Log.Warning(
                `Failed to read index file '${indexFilePath}' for IFrame widget '${this.name}' (widget id=${this.id})`,
                null,
                __fileline
            );

            return;
        }

        Log.Debug(
            `Parsing index file '${indexFilePath}', adding base, and storing srcdoc for IFrame widget '${this.name}' (widget id=${this.id})`,
            null,
            __fileline
        );

        const parser = new DOMParser();
        const parsed = parser.parseFromString(await indexFileContents.text(), 'text/html');
        const base = document.createElement('base');
        base.setAttribute('href', `https://sites.reach.local/${encodedOriginUrl}/`);
        parsed.head.prepend(base);

        this.setSrcDocContent(parsed);
    }

    async beforeFadeIn(): Promise<Partial<FadeInCommand> | void> {
        this.#postLoginActionsRan = false;
    }

    /**
     * Attempt to load the iframe into the page.
     */
    async getReadyForDisplay(): Promise<void> {
        this.#postLoginActionsRan = false;

        void this.attemptIframeLoad();
    }

    /**
     * Refreshes the IFrame
     */
    async schedulerAction(): Promise<void> {
        Log.Debug('IframeWidget schedulerAction!', null, __fileline);

        const now = Date.now();

        //if action is due in less than half a minute, run now
        if (!isNullish(this.#nextRefreshAt) && this.#nextRefreshAt - now < 30_000) {
            Log.Debug(`Refreshing IFrame '${this.name}' (widget id=${this.id})`, null, __fileline);

            this.attemptIframeLoad().finally(() => {
                this.#nextRefreshAt = Date.now() + this.config.refresh_interval * 60_000;
            });
        }

        //if action is due in less than half a minute, run now
        if (!isNullish(this.#nextZipFileUpdateAt) && this.#nextZipFileUpdateAt - now < 30_000) {
            Log.Debug(
                `Updating source zip file for IFrame '${this.name}' (widget id=${this.id})`,
                null,
                __fileline
            );

            void this.updateRunFromZipSource();

            this.#nextZipFileUpdateAt = Date.now() + ZIP_FILE_UPDATE_INTERVAL;
        }
    }

    /**
     * Set up listener to respond to any errors from this iframe on Reach Player
     */
    handleFrameErrors(): void {
        if (typeof REACH !== 'undefined' && REACH.CEF && REACH.CEF.RegisterFrameEventCallback) {
            REACH.CEF.RegisterFrameEventCallback(
                regExpEscape(this.config.url),
                (eventAsJson: string) => {
                    Log.Warning('illegal frame event received', null, __fileline, {
                        event: eventAsJson,
                    });
                }
            );
        }
    }

    /**
     * Attempt to load iframe using the virtualIframe.
     */
    async attemptIframeLoad(): Promise<void> {
        if (this.#virtualIframe) {
            await this.#virtualIframe.attemptIframeLoad();
        }
    }

    /**
     * Detect if loading of iframe is permitted.
     * - Resolve if iframe is permitted.
     * - Reject with reason if not permitted.
     * @returns {Promise.<string>|Promise.<Error>}
     */
    async isFramePermitted(): Promise<void> {
        //if we're running the IFrame from a zip file, we don't need to perform this check
        if (
            !isNullish(this.#srcDocContent) ||
            this.config.run_from_zip ||
            this.config.url.startsWith('https://sites.reach.local')
        ) {
            return;
        }

        /**
         * Confirm that the URL can be reached and that it does not
         *   have an X-Frame-Options header
         * ignored promise.
         */
        let xFrameOptionsOk = true;
        try {
            const content = await getAcquireUrlHeaders({ url: this.config.url });
            const headers = content.headers;
            //DENY, SAMEORIGIN
            const messagePart =
                'load iframe because the header (x-frame-options), ' +
                'fetched from the url, specifies that we cannot load ' +
                'this site here in an iframe';

            if (typeof headers['x-frame-options'] === 'string') {
                const parts = headers['x-frame-options'].split(' ', 2);
                switch (parts[0].toLowerCase()) {
                    case 'allow-from': //ALLOW-FROM URI
                        xFrameOptionsOk =
                            parts[1] && window.location.href === parts[1] ? true : false;
                        break;
                    default:
                        // only the reach player can sometimes load iframes with
                        // x-frame-options properly
                        if (Signage.platform instanceof Platform.ReachPlayer) {
                            Log.Warning(
                                `May not be able to ${messagePart}`,
                                null,
                                __fileline,
                                null
                            );
                        } else {
                            Log.Error(`Cannot ${messagePart}`, null, __fileline, null);
                            xFrameOptionsOk = false;
                        }
                        break;
                }
            }
        } catch (err) {
            /**
             * rejection may occur if:
             * 1. service failure on API (>3xx response)
             * 2. network down (cannot connect to API)
             * 3. API was unable to connect to target site for any reason
             * (DNS failure, host not responding, etc.)
             * 4. API failed to send a response to us in a timely manner
             *
             * If a rejection occurs, we cannot reliably know why or if this
             * means that the iframe will not work so we will optimistically
             * attempt to iframe it in
             */
            Log.Error('Is frame permitted error.', null, __fileline, err);
        }

        /**
         * NOTE: we cannot directly ajax the page because CORs
         *       will probably not allow it... perhaps we can
         *       have C# execute a request for us?
         * $.ajax(this.config.url).then(function(response) {
         *    setIframe();
         * , function(jqXHR, textStatus, errorThrown) {
         *    //failed to get the page for some reason
         *    setWarningDiv(textStatus);
         * );
         */
        if (!xFrameOptionsOk) {
            Log.Error(`An x-frame-options violation in isFramePermitted `, null, __fileline, null);
            throw new Error('x-frame-options violation');
        }
    }

    /**
     * Add iframe to the DOM, adding content if applicable, and performing
     *   any required login actions.
     */
    async setIframe(): Promise<void> {
        const iFrameHtml = this.createIframe();
        iFrameHtml.style.visibility = 'hidden';

        // If there is an existing iFrame, remove it!
        this.wrapperElement.querySelectorAll('.the-iframe').forEach((elm) => elm.remove());

        this.wrapperElement.appendChild(iFrameHtml);

        if (!this.config.url) {
            this.hasContentToPlay = false;
        } else {
            this.hasContentToPlay = true;
            if (this.#srcDocContent !== null) {
                iFrameHtml.srcdoc = this.#srcDocContent.documentElement.innerHTML;
            } else {
                iFrameHtml.src = this.config.url;
            }
        }

        await asyncTimeout(2000);

        iFrameHtml.style.visibility = 'visible';

        // do not try to run actions if auto login is not configured.
        if (!this.#passwordQuery) {
            return;
        }

        await asyncTimeout(10 * 1000);

        // check if the reloaded page is already logged in. If it is, we will
        // want to run the post login actions (if not already ran)
        if (
            iFrameHtml.querySelectorAll(this.#passwordQuery).length === 0 &&
            !this.#postLoginActionsRan
        ) {
            // ignored promise.
            this.performPostLoginActions();
        }
    }

    /**
     * Create an HTMLIframeElement including styles.
     */
    createIframe(): HTMLIFrameElement {
        const iFrameHtml = document.createElement('iframe');
        const iFrameId = `widget_iframe_${this.id}`;
        iFrameHtml.id = iFrameId;
        iFrameHtml.classList.add('the-iframe');
        iFrameHtml.style.border = 'none';
        // TODO: find alternative to scrolling
        iFrameHtml.scrolling = 'no';
        iFrameHtml.name = iFrameId;

        const cssStyles = this.getIframeCSS(iFrameHtml);
        Object.entries(cssStyles).forEach(([key, value]) => {
            if (key === 'height' || key === 'width') {
                iFrameHtml[key] = value.toString();
                iFrameHtml.style.setProperty(key, `${value}px`);
            } else {
                iFrameHtml.style.setProperty(key, value.toString());
            }
        });

        return iFrameHtml;
    }

    /**
     * @todo write description
     */
    getIframeCSS(iframeHtml?: HTMLElement): {
        height: number;
        transform: string;
        width: number;
    } {
        let transform = '';
        let width = this.#zoneWidth;
        let height = this.#zoneHeight;

        if (this.config.x_scroll_offset || this.config.y_scroll_offset) {
            transform = `translate(-${this.config.x_scroll_offset}px, -${this.config.y_scroll_offset}px)`;
            width += this.config.x_scroll_offset;
            height += this.config.y_scroll_offset;
        }

        if (this.config.scale && this.config.scale !== 1 && this.config.scale > 0) {
            if (iframeHtml) {
                iframeHtml.classList.add('the-iframe-scaled');
            }
            let scale = this.config.scale;
            scale = scale <= 4 ? scale : 4;

            const iframeWidth = Math.ceil(width * (1 / scale));
            const iframeHeight = Math.ceil(height * (1 / scale));

            //Modify IFrame Widget
            transform += ` scale(${scale})`;
            height = iframeHeight;
            width = iframeWidth;
        }

        return { transform: transform, width: width, height: height };
    }

    /**
     * Display warning with reason why iframe could not load
     */
    setWarningDiv(reason: string, message?: string): void {
        message = message || `<img src="/images/iframe-warning.png" /><p>(${reason})</p>`;
        const div = document.createElement('div');
        div.id = `iframe_warning_${this.id}`;
        div.classList.add('the-iframe-warning');
        div.innerHTML = message;

        this.wrapperElement.innerHTML = '';
        this.wrapperElement.appendChild(div);
    }

    /**
     * Perform various actions dictated by the config.post_login_actions
     */
    async performPostLoginActions(): Promise<void> {
        this.#postLoginActionsRan = true;

        /**
         * If it doesn't exist let the rest of the program continue, because no
         *  post login actions exist.
         */
        if (
            !this.config.post_login_actions ||
            (this.config.post_login_actions && this.config.post_login_actions.length === 0)
        ) {
            return;
        }

        // restart action list (getting to this point in the code means that we
        // detected that we need to login now)
        let actions: { type: string; params: string }[] = [];
        try {
            actions = JSON.parse(this.config.post_login_actions);
        } catch (ignoredError) {
            // Ignore the error, because it is an invalid post_login_actions
            return;
        }

        // ignored promise.
        this.postLoginNextAction(actions);
    }

    /**
     * Handle individual post login actions one at a time.
     */
    async postLoginNextAction(actions: { type: string; params: string }[]): Promise<void> {
        // all of the actions have been run
        if (!actions.length) {
            return;
        }

        const currentActionPromise = new Promise<void>((resolve) => {
            try {
                if (actions[0].type === 'clickWhenOnDOM') {
                    // create a watcher to watch for the target specified by
                    // the params to be in the DOM
                    const targetElementWatcher = setInterval(async () => {
                        if (
                            this.#virtualIframe &&
                            (await this.#virtualIframe.executeJS(
                                `document.querySelector("${actions[0].params}") !== null`,
                                true
                            )) === true
                        ) {
                            // target element has been found
                            clearInterval(targetElementWatcher);

                            // prevent links from opening in another tab
                            await this.#virtualIframe.executeJS(
                                `document.querySelector("${actions[0].params}").removeAttribute("target")`
                            );

                            // wait for DOM to update
                            await asyncTimeout(100);
                            if (this.#virtualIframe) {
                                await this.#virtualIframe.executeJS(
                                    `document.querySelector("${actions[0].params}").click()`
                                );
                            }
                            resolve();
                        }
                    }, 1000);
                } else {
                    Log.Warning(
                        `post_login_actions "${actions[0].type}" does not have an action defined to execute.`,
                        null,
                        __fileline,
                        null
                    );
                }
            } catch (error) {
                Log.Error(
                    `Failed to execute post_login_actions "${actions[0].type}" ` +
                        `with params ${actions[0].params} | `,
                    null,
                    __fileline,
                    error
                );
                resolve();
            }
        });

        // ignored promise.
        currentActionPromise.then(() => {
            actions.shift();
            // ignored promise.
            this.postLoginNextAction(actions);
        });
    }

    /**
     * Start a script to log in to page loaded in iframe
     * - will attempt re-login if user gets logged out automatically
     */
    async startLoginScript(): Promise<void> {
        if (this.config.login_sequence && this.config.login_sequence !== 'None') {
            // ignored promise.
            ReachAPI.widget
                .getIframeLoginSequence(this.config.login_sequence)
                .then((data: { data: { config: string }[] }) => {
                    try {
                        this.#sequenceConfig = JSON.parse(data.data[0].config);
                    } catch (parseError) {
                        Log.Error(
                            `Login Sequence failed to parse config json. Error: ${parseError}`,
                            null,
                            __fileline,
                            parseError
                        );
                        throw parseError;
                    }

                    if (
                        !this.#sequenceConfig ||
                        !this.#sequenceConfig.login ||
                        !this.#sequenceConfig.login.loginLogic
                    ) {
                        throw new Error('Missing sequenceConfig data.');
                    }

                    // ignored promise.
                    this.getLoginLogicPromise(this.#sequenceConfig.login.loginLogic).then(
                        (loginLogicData) => {
                            if (loginLogicData.data.length) {
                                this.logicData = loginLogicData.data[0];

                                // ignored promise.
                                this.getPreLoginPromise()
                                    .then(async () => {
                                        return this.getLoginPromise();
                                    })
                                    .then(async () => {
                                        return this.getPostLoginPromise();
                                    })
                                    .then(async () => {
                                        // try {
                                        return this.startLoginWatcher();
                                    })
                                    .catch(() => {
                                        Log.Info('Login sequence complete', null, __fileline, null);
                                        /**
                                         * The whole login sequence is now complete.
                                         * Now start the watcher process that will use the
                                         *   verification steps to determine which page/login
                                         *   we are on and rerun those page/steps back to the
                                         *   final state we want to be on.
                                         */
                                        return this.startLoginWatcher();
                                    });
                            } else {
                                Log.Error(
                                    `Unable to find requested login logic named: ${this.config.login_logic}`,
                                    null,
                                    __fileline,
                                    null
                                );
                            }
                        }
                    );
                });
        } else if (this.config.login_logic && this.config.login_logic !== 'None') {
            /**
             * None, or empty string means do not try to login
             * ignored promise.
             */
            this.getLoginLogicPromise(this.config.login_logic).then(async (data) => {
                if (!data.data || !data.data[0]) {
                    Log.Error(
                        `Unable to find requested login logic named: ${this.config.login_logic}`,
                        null,
                        __fileline,
                        null
                    );
                    return;
                }

                const actualData = data.data[0];
                const usernameQuery = actualData.username_query;
                const loginButtonQuery = actualData.login_button_query;
                const afterBodyWaitTime = actualData.after_body_wait_time;
                const passwordQuery = actualData.password_query;
                const signInButtonQuery = actualData.sign_in_button_query || '';
                const usernameContinueButtonQuery = actualData.username_continue_button_query || '';
                const isTwoStepVerification = !!signInButtonQuery;

                // make available for the post_login_actions
                this.#passwordQuery = passwordQuery;

                // worker to watch for when the iframe has loaded
                const waitUntilLoaded = window.setInterval(async () => {
                    const bodyLoaded =
                        (await this.#virtualIframe?.executeJS(
                            'document.querySelector("body") !== null',
                            true
                        )) === true;

                    if (bodyLoaded) {
                        clearInterval(waitUntilLoaded);

                        // wait until any JS on the page has run
                        await asyncTimeout(afterBodyWaitTime);
                        // ignored promise.
                        this.performLogin(
                            isTwoStepVerification,
                            signInButtonQuery,
                            usernameQuery,
                            usernameContinueButtonQuery,
                            passwordQuery,
                            loginButtonQuery
                        );

                        // create a watcher to watch and re-login if the user gets auto-logged-out
                        setInterval(async () => {
                            // seeing that the password field exists is a good indicator that we
                            //   are back to the login page again
                            if (
                                (await this.#virtualIframe?.executeJS(
                                    `document.querySelector("${passwordQuery}") !== null`,
                                    true
                                )) === true
                            ) {
                                // ignored promise.
                                this.performLogin(
                                    isTwoStepVerification,
                                    signInButtonQuery,
                                    usernameQuery,
                                    usernameContinueButtonQuery,
                                    passwordQuery,
                                    loginButtonQuery
                                );
                            } else if (
                                isTwoStepVerification &&
                                (await this.#virtualIframe?.executeJS(
                                    `document.querySelector("${signInButtonQuery}") !== null`,
                                    true
                                )) === true
                            ) {
                                /**
                                 * If the login login is a two-step verification, it is
                                 *   better to look for the signIn button to determine
                                 *   if we need to login again
                                 * ignored promise.
                                 */
                                this.performLogin(
                                    isTwoStepVerification,
                                    signInButtonQuery,
                                    usernameQuery,
                                    usernameContinueButtonQuery,
                                    passwordQuery,
                                    loginButtonQuery
                                );
                            }
                        }, 1000);
                    }
                }, 100);
            });
        }
    }

    private async performLogin(
        isTwoStepVerification: boolean,
        signInButtonQuery: string,
        usernameQuery: string,
        usernameContinueButtonQuery: string,
        passwordQuery: string,
        loginButtonQuery: string
    ): Promise<void> {
        if (isTwoStepVerification) {
            try {
                await this.#virtualIframe?.executeJS(
                    `document.querySelector("${signInButtonQuery}").click()`
                );
            } catch (ignoredError) {
                // The original developer failed to document what
                // exception they were trying to catch, so it is unknown
                // why this try/catch block is here.
                Log.Error(`Some uncaught error no documentation.`, null, __fileline, ignoredError);
            }
            try {
                await this.#virtualIframe?.executeJS(
                    `document.querySelector("${usernameQuery}").value = "${this.config.username}"`
                );
            } catch (ignoredError) {
                // if we do not find the username field we still want to
                // continue so that we can continue the loop and retry
                // the performLogin
                Log.Error(`Some uncaught error no documentation.`, null, __fileline, ignoredError);
            }
            try {
                await this.#virtualIframe?.executeJS(
                    `document.querySelector("${usernameContinueButtonQuery}").click()`
                );
            } catch (ignoredError) {
                // The original developer failed to document what
                // exception they were trying to catch, so it is unknown
                // why this try/catch block is here.
                Log.Error(`Some uncaught error no documentation.`, null, __fileline, ignoredError);
            }
        } else {
            try {
                await this.#virtualIframe?.executeJS(
                    `document.querySelector("${usernameQuery}").value = "${this.config.username}"`
                );
            } catch (ignoredError) {
                // The original developer failed to document what
                // exception they were trying to catch, so it is unknown
                // why this try/catch block is here.
                Log.Error(`Some uncaught error no documentation.`, null, __fileline, ignoredError);
            }
        }

        try {
            await this.#virtualIframe?.executeJS(
                `document.querySelector("${passwordQuery}").value = "${this.config.password}"`
            );
        } catch (ignoredError) {
            // if we do not find the password field we still want to
            // continue so that we can continue the loop and retry the
            // performLogin
            Log.Error(`Some uncaught error no documentation.`, null, __fileline, ignoredError);
        }
        try {
            // prevent links from opening in another tab
            await this.#virtualIframe?.executeJS(
                `document.querySelector("${loginButtonQuery}").removeAttribute("target")`
            );

            // wait for DOM to update
            await asyncTimeout(100);
            await this.#virtualIframe?.executeJS(
                `document.querySelector("${loginButtonQuery}").click()`
            );

            await asyncTimeout(1000);
            // ignored promise.
            this.performPostLoginActions();
        } catch (ignoredError) {
            // if we do not find the submit button we still want to
            // continue so that we can continue the loop and retry the
            // performLogin
            Log.Error(`Some uncaught error no documentation.`, null, __fileline, ignoredError);
        }
    }

    /**
     * Returns the promise that executes the steps and verification done on the
     * pre-login step. Rejects the promise if the verification step fails.
     */
    async getPreLoginPromise(): Promise<void> {
        if (!this.#sequenceConfig?.preLogin) {
            return;
        }

        // pre-login always needs a verification property (to know the page has loaded)
        try {
            if (this.#sequenceConfig.preLogin.steps) {
                await this.runSteps(this.#sequenceConfig.preLogin.steps);
            }
        } catch (ignoreError) {
            // ignore error
        }

        await this.processVerification(this.#sequenceConfig.preLogin.verification);
    }

    /**
     * Returns the promise that executes the steps and verification done on the main
     * login step. Rejects the promise if the verification step fails.
     */
    async getLoginPromise(): Promise<void> {
        // main login steps
        let steps:
            | (
                  | StepSetValue
                  | StepExecuteOnElement
                  | StepExecuteJs
                  | StepClick
                  | StepWait
                  | StepWaitForElements
              )[]
            | undefined = [];
        if (
            this.#sequenceConfig?.login.loginSteps &&
            this.#sequenceConfig.login.loginSteps === 'standardUnPw'
        ) {
            steps = [
                {
                    // wait for the JS on the page to run
                    type: SequenceStepTypes.WAIT,
                    params: {
                        selector: '$(logicData.username_query)',
                        value: '$(config.username)',
                        timeInSeconds: 5,
                    },
                },
                {
                    type: SequenceStepTypes.SET_VALUE,
                    params: {
                        selector: '$(logicData.username_query)',
                        value: '$(config.username)',
                        maxWaitSeconds: 3,
                    },
                },
                {
                    type: SequenceStepTypes.SET_VALUE,
                    params: {
                        selector: '$(logicData.password_query)',
                        value: '$(config.password)',
                        maxWaitSeconds: 3,
                    },
                },
                {
                    type: SequenceStepTypes.CLICK,
                    params: {
                        selector: '$(logicData.login_button_query)',
                        maxWaitSeconds: 3,
                    },
                },
                {
                    // default wait time for signin process to complete
                    type: SequenceStepTypes.WAIT,
                    params: {
                        selector: '',
                        value: '',
                        timeInSeconds: 5,
                    },
                },
            ];
        } else if (this.#sequenceConfig?.login.steps) {
            steps = this.#sequenceConfig.login.steps;
        }

        try {
            await this.runSteps(steps);

            if (this.#sequenceConfig?.login.verification) {
                await this.processVerification(this.#sequenceConfig.login.verification);
            }
        } catch (ignoreError) {
            // Ignore the error the original did.
        }
    }

    /**
     * Returns the promise that executes the steps and verification done on the
     * post-login step. Rejects the promise if the verification step fails.
     */
    async getPostLoginPromise(): Promise<void> {
        if (
            !this.#sequenceConfig?.postLogin ||
            (this.#sequenceConfig.postLogin && !this.#sequenceConfig.postLogin.steps)
        ) {
            return;
        }

        if (this.#postLoginActionsRan) {
            return;
        }

        try {
            await this.runSteps(this.#sequenceConfig.postLogin.steps);
            this.#postLoginActionsRan = true;
        } catch (ignoreError) {
            // Ignore error and try processVerification
        }

        // TODO:  again postLogin.verification doesn't exist...
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (this.#sequenceConfig.postLogin.verification) {
            await this.processVerification(
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                this.#sequenceConfig.postLogin.verification
            );
        }
    }

    /**
     * Starts a process that will run in 30 seconds (to prevent thrashing). This
     * process uses the verification steps to determine which login step we are
     * currently at. If it is detected that we are at a previous step, it will
     * automatically run the steps on and after the step it thinks that it is on.
     */
    async startLoginWatcher(): Promise<void> {
        // add a delay so that we do not get into a quick loop of trying over and over
        await asyncTimeout(1000 * 30);

        if (
            !this.#sequenceConfig ||
            (this.#sequenceConfig && !this.#sequenceConfig.preLogin.verification)
        ) {
            Log.Warning('No preLogin verification.', null, __fileline, null);
            return;
        }

        try {
            /**
             * Try to go through all of the steps to login, verifying the user
             */
            await this.processVerification(this.#sequenceConfig.preLogin.verification);

            // seems that we are at the second login sequence step now, so start from there
            try {
                await this.getLoginPromise();

                try {
                    await this.getPostLoginPromise();
                } catch (ignoreError) {
                    // ignore error
                }
            } catch (ignoreError) {
                // ignore error
            }

            try {
                await this.startLoginWatcher();
            } catch (ignoreError) {
                // ignore error
            }
        } catch (err) {
            /**
             * If the user hasn't been verified yet, try to run this function again.
             */
            try {
                if (!this.#sequenceConfig?.login || !this.#sequenceConfig?.login.verification) {
                    Log.Error('No login verification.', null, __fileline, null);
                    throw new Error('No login verification.');
                }

                await this.processVerification(this.#sequenceConfig.login.verification);

                /**
                 * Seems that we are at the third login sequence step now
                 * ignored promise.
                 */
                this.getPostLoginPromise()
                    .then(this.startLoginWatcher)
                    .catch(this.startLoginWatcher);
            } catch (ignoreError) {
                // ignored promise.
                this.startLoginWatcher();
            }
        }
    }

    /**
     * Processes the given verification. Rejects the returned promise if the
     * verification fails
     */
    async processVerification(verification: LoginVerification): Promise<void> {
        if (
            verification.type &&
            (verification.type === 'standardUnPw' || verification.type === 'elementsFound')
        ) {
            if (verification.type === 'standardUnPw') {
                verification.type = 'elementsFound';
                if (!verification.params) {
                    verification.params = {
                        elements: [],
                    };
                }

                verification.params.elements = [
                    '$(logicData.username_query)',
                    '$(logicData.password_query)',
                    '$(logicData.login_button_query)',
                ];
            }

            // default max wait seconds
            let waitSeconds = 300;

            if (verification.params.maxWaitSeconds) {
                waitSeconds = verification.params.maxWaitSeconds;
            }

            // ignored promise.
            this.createStepPromise({
                type: SequenceStepTypes.WAIT_FOR_ELEMENTS,
                params: {
                    elements: verification.params.elements,
                    maxWaitSeconds: waitSeconds,
                },
            });
        } else {
            Log.Warning(
                `Login Sequence Verification Type "${verification.type}" not found.`,
                null,
                __fileline,
                null
            );
        }
    }

    /**
     * creates the promise needed to do the work instructed by the contents of the
     * step object passed in
     */
    async createStepPromise(
        step:
            | StepSetValue
            | StepExecuteOnElement
            | StepExecuteJs
            | StepClick
            | StepWait
            | StepWaitForElements
    ): Promise<void> {
        // change the FN descriptor to fulfilStepPromise
        switch (step.type) {
            case SequenceStepTypes.SET_VALUE:
                // ignored promise.
                this.sequenceSetValue(step);
                break;
            case SequenceStepTypes.EXECUTE_ON_ELEMENT:
                // ignored promise.
                this.sequenceExecuteOnElement(step);
                break;
            case SequenceStepTypes.EXECUTE_JS:
                // ignored promise.
                this.sequenceExecuteJs(step);
                break;
            case SequenceStepTypes.CLICK:
                // ignored promise.
                this.sequenceClick(step);
                break;
            case SequenceStepTypes.WAIT:
                await asyncTimeout(step.params.timeInSeconds * 1000);
                break;
            case SequenceStepTypes.WAIT_FOR_ELEMENTS:
                // ignored promise.
                this.sequenceWaitForElements(step);
                break;
            default:
                Log.Warning(`Login Sequence Step Type not found.`, null, __fileline, step);
                break;
        }
    }

    private async sequenceSetValue(step: StepSetValue): Promise<void> {
        try {
            await this.#virtualIframe?.executeJS(
                `document.querySelector("${this.computeQueryValue(
                    step.params.selector
                )}").value = "${this.computeQueryValue(step.params.value)}"`
            );
        } catch (ignoreError) {
            // The original developer failed to document what exception
            // they were trying to catch, so it is unknown why this
            // try/catch block is here.
        }

        // wait a bit for the value to be set
        await asyncTimeout(100);

        try {
            // blur so that the form validators can be triggered
            await this.#virtualIframe?.executeJS(
                `document.querySelector("${this.computeQueryValue(step.params.selector)}").blur()`
            );
        } catch (ignoreError) {
            // just want to try to trigger validators and don't care
            // if they don't exist
        }

        try {
            // some logins do validations on the onchange event
            await this.#virtualIframe?.executeJS(
                `document.querySelector("${this.computeQueryValue(
                    step.params.selector
                )}").onchange()`
            );
        } catch (ignoreError) {
            // just want to try to trigger validators and don't care
            // if they don't exist
        }
    }

    private async sequenceExecuteOnElement(step: StepExecuteOnElement): Promise<void> {
        try {
            await this.#virtualIframe?.executeJS(
                `document.querySelector("${this.computeQueryValue(
                    step.params.selector
                )}").${this.computeQueryValue(step.params.value)}`
            );
        } catch (ignoreError) {
            // The original developer failed to document what exception
            // they were trying to catch, so it is unknown why this
            // try/catch block is here.
        }
    }

    private async sequenceExecuteJs(step: StepExecuteJs): Promise<void> {
        try {
            await this.#virtualIframe?.executeJS(this.computeQueryValue(step.params.value));
        } catch (ignoreError) {
            // The original developer failed to document what exception
            // they were trying to catch, so it is unknown why this
            // try/catch block is here.
        }
    }

    private async sequenceClick(step: StepClick): Promise<void> {
        try {
            // prevent links from opening in another tab
            await this.#virtualIframe?.executeJS(
                `document.querySelector("${this.computeQueryValue(
                    step.params.selector
                )}").removeAttribute("target")`
            );
        } catch (ignoreError) {
            // The original developer failed to document what exception
            // they were trying to catch, so it is unknown why this
            // try/catch block is here.
        }
        try {
            await this.#virtualIframe?.executeJS(
                `document.querySelector("${this.computeQueryValue(step.params.selector)}").click()`
            );
        } catch (ignoreError) {
            // The original developer failed to document what exception
            // they were trying to catch, so it is unknown why this
            // try/catch block is here.
        }
    }

    private async sequenceWaitForElements(step: StepWaitForElements): Promise<void> {
        let waitUntilLoaded = window.setInterval(async () => {
            let elementsLoaded =
                (await this.#virtualIframe?.executeJS(
                    'document.querySelector("body") !== null',
                    true
                )) === true;

            // for loop does not work on json serialized objects for some reason
            const len = step.params.elements.length;
            let i = 0;
            while (i < len) {
                if (!elementsLoaded) {
                    // one of the elements is not loaded, no need to continue
                    break;
                }
                elementsLoaded =
                    elementsLoaded &&
                    (await this.#virtualIframe?.executeJS(
                        `document.querySelector("${this.computeQueryValue(
                            step.params.elements[i]
                        )}") !== null`,
                        true
                    )) === true;
                i++;
            }

            if (elementsLoaded) {
                clearInterval(waitUntilLoaded);
                waitUntilLoaded = 0;
            }
        }, 100);

        await asyncTimeout(step.params.maxWaitSeconds * 1000);

        if (waitUntilLoaded !== 0) {
            // kill worker, worker did not complete before max time reached
            clearInterval(waitUntilLoaded);
        }
    }

    /**
     * Replaces items in a string wrapped in $() with variables from the iframe
     * entity. Mainly used for accessing loginLogic fields.
     */
    computeQueryValue(value: string): string {
        const magicSelectorIdx = value.indexOf('$(');
        let ret = value;
        if (magicSelectorIdx !== -1) {
            const endOfMagicSelectorIdx = value.indexOf(')', magicSelectorIdx);
            const target = value.substring(magicSelectorIdx + 2, endOfMagicSelectorIdx).split('.');
            ret =
                value.slice(0, magicSelectorIdx) +
                // TODO: I'm not sure how to convert this...
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                String(this[target[0]][target[1]]) +
                value.slice(endOfMagicSelectorIdx + 1);

            return this.computeQueryValue(ret);
        }

        return ret;
    }

    /**
     * gets the login logic either from hard-coded or the database
     */
    async getLoginLogicPromise(loginLogic: string): Promise<{ data: LoginLogicData[] }> {
        let usernameQuery;
        let passwordQuery;
        let loginButtonQuery;
        let signInButtonQuery;
        let usernameContinueButtonQuery;
        // const jqDeferred = $.Deferred();
        // let jqPromise = jqDeferred.promise();
        let data: { data: LoginLogicData[] };

        if (loginLogic === 'Auto-Detect' || loginLogic === 'Two Step Auth') {
            // originally created for google login
            if (loginLogic === 'Two Step Auth') {
                // selector "form li:last > div" is used to click on the sign in as another
                //   user button on the google login
                signInButtonQuery = 'form li:last-child > div';
                // button is meant to be pressed after the username is typed in (first step)
                usernameContinueButtonQuery = '#identifierNext';
            }

            // contains queries for common attributes found in username/password/login fields
            usernameQuery =
                "input[placeholder='Username'], input[placeholder='username'], " +
                "input[placeholder='Email address'], input[placeholder='Email Address'], " +
                "input[name='Username'], input[name='username'], input[name='emailAddress'], " +
                "#Username, #username, input[placeholder='User'], input[placeholder='user'], " +
                "input[name='User'], input[name='user'], #User, #user, input[type='email']";
            passwordQuery =
                "input[placeholder='Password'], input[placeholder='password'], " +
                "input[name='Password'], input[name='password'], #Password, #password, " +
                "input[type='password']";
            loginButtonQuery =
                "input[value='Login'], input[value='Sign In'], input[value='sign in'], " +
                "input[value='Sign in'], input[value='Log in'], input[value='Sign In'], " +
                "button[type='submit'], input[type='submit'], #passwordNext";

            data = {
                data: [
                    {
                        username_query: usernameQuery,
                        password_query: passwordQuery,
                        login_button_query: loginButtonQuery,
                        after_body_wait_time: 500,
                        sign_in_button_query: signInButtonQuery,
                        username_continue_button_query: usernameContinueButtonQuery,
                    },
                ],
            };
            return data;
        } else {
            return ReachAPI.widget.getIframeLoginLogic(loginLogic);
        }
    }

    /**
     * Run a sequence of login related steps, if there are no steps the
     *   function will return.
     * @param {Array} steps
     * @returns {Promise}
     */
    async runSteps(
        steps:
            | (
                  | StepSetValue
                  | StepExecuteOnElement
                  | StepExecuteJs
                  | StepClick
                  | StepWait
                  | StepWaitForElements
              )[]
            | undefined
    ): Promise<void> {
        if (!steps || (steps && steps.length === 0)) {
            return;
        }

        try {
            // TODO: Should a max attempt be set to break out of this loop?
            // Magic used to make sure that the promises get run one after the other:
            return steps.reduce((previousPromise, nextStep) => {
                const nextPromise = async () => {
                    await this.createStepPromise(nextStep);
                };
                return previousPromise.then(nextPromise, nextPromise);
            }, Promise.resolve());
        } catch (err) {
            Log.Debug(
                `IframeWidget.runSteps rejected, but should always resolve`,
                null,
                __fileline,
                err
            );
        }
    }

    /**
     * Calls virtualIframe render then calls super play with the passed in slot.
     */
    async play(currentSlot: TimelineSlot): Promise<void> {
        /**
         * Additional WindowedIframe check added to prevent
         *   a render loop, where it would continually re-render
         *   the iframe completely, refreshing the page.
         */
        if (
            this.#virtualIframe &&
            this.#virtualIframe instanceof WindowedIframe &&
            this.#virtualIframe.handle < 0
        ) {
            await this.#virtualIframe?.render();
        } else if (this.#shouldBeRefreshing) {
            await this.#virtualIframe?.render();
        }

        await super.play(currentSlot);
    }

    /**
     * Calls the virtualIframe destroy function.
     */
    protected async _doPause(): Promise<void> {
        await this.#virtualIframe?.destroy();
    }

    /**
     * Calls the virtualIframe destroy function, then runs super stop.
     */
    async stop(): Promise<void> {
        await this.#virtualIframe?.destroy();
        await super.stop();
    }

    /**
     * basically a factory function that returns a new instance of a class that
     * augments (or is) VirtualIframe. This choice is based on the platform and the
     * options from the iframe configuration
     * @returns {Promise}
     */
    static createInstance(widget: IframeWidget): WindowedIframe | VirtualIframe {
        // todo make deciding logic based on player type
        if (widget.config.windowed) {
            return new WindowedIframe(widget);
        }

        return new VirtualIframe(widget);
    }
}
